import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';
import type { WorldCupBookmakerMarkets, WorldCupTeam } from './use-world-cup-gamelines';

export interface WorldCupHalftimeData {
  slug: string;
  title?: string;
  home_team?: WorldCupTeam;
  away_team?: WorldCupTeam;
  game_start_time?: string;
  game_event_id?: string | null;
  halftime: WorldCupBookmakerMarkets | null;
}

export interface WorldCupHalftimeResponse {
  success: boolean;
  data: WorldCupHalftimeData;
  message?: string;
}

export interface UseWorldCupHalftimeOptions {
  eventSlug?: string | null;
  enabled?: boolean;
  swr?: SWRConfiguration<WorldCupHalftimeResponse, Error>;
}

export async function fetchWorldCupHalftime(eventSlug: string) {
  const searchParams = new URLSearchParams({
    event_slug: eventSlug,
  });

  const response = await customFetch(`/api/world-cup/game/halftime?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch World Cup halftime');
  }

  const result = (await response.json()) as WorldCupHalftimeResponse;

  if (!result.success) {
    throw new Error(result.message || 'Failed to fetch World Cup halftime');
  }

  return result;
}

export function useWorldCupHalftime(options: UseWorldCupHalftimeOptions = {}) {
  const eventSlug = options.eventSlug?.trim();
  const enabled = options.enabled ?? true;
  const swrKey = enabled && eventSlug ? ['world-cup-halftime', eventSlug] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<WorldCupHalftimeResponse, Error>(
    swrKey,
    () => fetchWorldCupHalftime(eventSlug!),
    {
      revalidateOnFocus: false,
      ...options.swr,
    },
  );

  return {
    data: data ?? null,
    halftime: data?.data?.halftime ?? null,
    detail: data?.data ?? null,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

export default useWorldCupHalftime;
